import { useQuery } from '@apollo/client';
import { Option } from '@prenuvo/shared-library';
import { createContext, ReactNode, useEffect, useState } from 'react';

// components
import { LoadingScreen, Message } from '../components';
// graphql
import { IS_AUTHENTICATED } from '../graphql/queries';
import {
  useCurrentUserQuery,
  useGetPractitionersByPrenuvoIds,
  useLocationsQuery,
  useNPsQuery,
} from '../hooks';
// types
import { Location, NursePractitioner, User } from '../types';

// @types
export type AuthContextType = {
  allowedLocations: Array<Location>;
  allowedPrescriberLocations: Array<Location>;
  handleSetView: (viewChange: string) => string;
  hasAccessToNPD: boolean;
  isAuthenticated: boolean;
  isNPCoordinatorOrPrescriber: boolean;
  npOptions: Array<Option>;
  nursePractitioners: Array<NursePractitioner>;
  setUserSignature: (signature: string) => void;
  user: undefined | User;
  userSignature: string;
  view: string;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode;
};

const AuthProvider = ({ children }: AuthProviderProps) => {
  // queries
  const { data, error, loading } = useQuery(IS_AUTHENTICATED);
  const { data: user, loading: userLoading } = useCurrentUserQuery();
  const { data: locations, loading: locationsLoading } = useLocationsQuery();
  const { data: nursePractitionerIds, loading: nursePractitionersLoading } = useNPsQuery();
  const { getPractitionersByPrenuvoIds } = useGetPractitionersByPrenuvoIds([]);
  const [nursePractitioners, setNursePractitioners] = useState<Array<NursePractitioner>>([]);
  const [userSignature, setUserSignature] = useState('');

  const npOptions = nursePractitioners.map((nursePractitioner: NursePractitioner) => ({
    label: `${nursePractitioner.firstName} ${nursePractitioner.lastName}`,
    value: nursePractitioner.prenuvoId,
  }));

  npOptions.sort((a, b) => a.label.localeCompare(b.label));

  const [view, setView] = useState<string>('');

  const isNP = user?.roles?.includes('np');
  const isNPCoordinator = user?.roles?.includes('np_coordinator');
  const isNPCoordinatorOrPrescriber = ['np_coordinator', 'prescriber'].some((role) =>
    user?.roles?.includes(role),
  );

  const handleSetView = (viewChange: string) => {
    if ((viewChange === 'np_coordinator' && isNPCoordinator) || (viewChange === 'np' && isNP)) {
      setView(viewChange);
      return viewChange;
    }
    if (isNP) {
      setView('np');
    }
    if (isNPCoordinator) {
      setView('np_coordinator');
    }
    return '';
  };

  const getNPFromHpd = async () => {
    if (!nursePractitionersLoading && nursePractitionerIds.length) {
      const npPrenuvoIds = nursePractitionerIds.map((np: { userId: string }) => np.userId);
      const { data } = await getPractitionersByPrenuvoIds({
        variables: {
          practitionersList: npPrenuvoIds,
        },
      });
      const formattedData = data.practitionersByIds.edges
        .map((e: any) => e.node)
        .map((np: any) => {
          const { signature, location, roles, prescriberLocations } = nursePractitionerIds.find(
            (npFromPii: { userId: string }) => npFromPii.userId === np.prenuvoId,
          );
          return {
            ...np,
            locationId: np?.locations?.edges[0]?.node?.id || '',
            signature: signature,
            allowedLocations: location,
            prescriberLocations: prescriberLocations,
            roles: roles,
          };
        });
      setNursePractitioners(formattedData);
    }
  };

  useEffect(() => {
    getNPFromHpd();
  }, [nursePractitionerIds]);

  if (loading || userLoading || locationsLoading || nursePractitionersLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return <Message>Something went wrong. Please try again later.</Message>;
  }

  const hasAccessToNPD = isNP || isNPCoordinator;

  const allowedLocationsIds = user?.location || [];

  const allowedLocations = allowedLocationsIds
    .map((locationId: string) =>
      locations.find(({ code }: { code: string }) => code === locationId),
    )
    .filter((location: Location) => location);

  const prescriberLocationIds = user?.prescriberLocations || [];

  const allowedPrescriberLocations = allowedLocations.filter(({ code }: { code: string }) =>
    prescriberLocationIds.some((countryState: string) => code.includes(countryState)),
  );

  return (
    <AuthContext.Provider
      value={{
        allowedLocations,
        allowedPrescriberLocations,
        handleSetView,
        hasAccessToNPD,
        isAuthenticated: data.isAuthenticated,
        isNPCoordinatorOrPrescriber,
        npOptions,
        nursePractitioners,
        setUserSignature,
        user,
        userSignature,
        view,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
